import { Injectable } from "@angular/core";
import { Capacitor } from "@capacitor/core";
import 'cordova-plugin-purchase';
import { ApiService } from "./api";
import { UserManager } from "./managers/user.manager";
import { AccountMembership } from "src/models/accountMembership";
import { Membership } from "src/models/membership";
import { LoadingController, ToastController } from "@ionic/angular";
import { AppInsightsService } from "./app-insights.service";
import { Organization } from "src/models/organization";
import { Router } from "@angular/router";


@Injectable({
  providedIn: 'root'
})
export class MembershipService {
  store: CdvPurchase.Store;
  redirect: any[];
  readonly nextGenOrgId = 58;
  private accountId: number;
  private rodeoId = 0;
  private loader?: HTMLIonLoadingElement;

  constructor(
    private api: ApiService,
    private loading: LoadingController,
    private userManager: UserManager,
    private toast: ToastController,
    private insights: AppInsightsService,
    private router: Router
  ) {
    this.userManager.account.subscribe(account => {
      this.accountId = account.id;
    });
  }

  private iapProducts: CdvPurchase.IRegisterProduct[] = [];

  //#region IAP
  async initInAppPurchase() {
    const { store } = CdvPurchase;
    const appPlatform = Capacitor.getPlatform();
    this.store = store;

    const platform = appPlatform == 'ios' ? CdvPurchase.Platform.APPLE_APPSTORE
      : appPlatform == 'android' ? CdvPurchase.Platform.GOOGLE_PLAY
        : CdvPurchase.Platform.WINDOWS_STORE;

    this.store.verbosity = CdvPurchase.LogLevel.DEBUG;

    this.buildProducts(platform); //windows store not supported yet

    this.store.register(this.iapProducts);

    // Register event handlers
    this.store.when()
      .productUpdated((product: CdvPurchase.Product) => {
        console.log('UPDATED: ', product);
        console.log('OWNED PRODUCT', product.owned);        
      })
      .approved((transaction: CdvPurchase.Transaction) => {
        console.log('APPROVED', transaction);
        //if android, check user memberships for this product id
        //if product id does not have purchasetoken, send to api
        if (platform == CdvPurchase.Platform.GOOGLE_PLAY 
          && transaction.products 
          && transaction.products.length > 0 
          && transaction.products[0].id.includes('memberships')) {
            this.fixForMissingAndroidPurchaseTokens(transaction);
        }

        this.insights.logEvent('IAP APPROVED', {
          transactionId: transaction.transactionId,
          accountId: this.accountId,
          platform: transaction.platform,
          validatorEndpoint: this.store.validator?.toString(),
          transaction
        });
        if (this.loader) {
          this.loader.present();
        }
        transaction.verify();
      })
      .verified((receipt: CdvPurchase.VerifiedReceipt) => {
        this.insights.logEvent('IAP VERIFY - SUCCESS', {
          receiptId: receipt.id,
          accountId: this.accountId,
          platform: receipt.platform
        });
        if (this.loader) {
          this.loader.dismiss();
        }
        console.log('VERIFIED', receipt);
        receipt.finish();
      })
      .unverified(order => {
        try {
          this.insights.logEvent('IAP VERIFY - FAILED', {
            order,
            accountId: this.accountId,
            platform: order.receipt?.platform
          });
        } finally {
          if (this.loader) {
            this.loader.dismiss();
          }
        }
        console.log("Sorry, transaction unverified");
      })
      .finished(transaction => {
        console.log('FINISHED', transaction);
        this.insights.logEvent('IAP FINISHED', {
          accountId: this.accountId,
          transaction,
          platform: transaction.platform
        });

        if (this.loader) {
          this.toast.create({
            color: 'success',
            message: 'Membership Purchased!',
            duration: 2000,
            position: 'bottom'
          }).then(t => {
            t.present();
            if (this.redirect) {
              this.router.navigate(this.redirect).then(() => this.redirect = undefined);
            }
          });
        } else {
          //this is a receipt verification invoked on app start, not purchased by user
        }
      });

    // Track all store errors
    this.store.error((err) => {
      console.error('Store Error', JSON.stringify(err));
      this.insights.logException(new Error(`IAP ERROR ${err.code} - ${err.message}`));
      
      //handle init errors
      if (err.code == 6777001) {
        this.toast.create({
          color: 'danger',
          message: 'Payment service unavailable. Please try again later.',
          duration: 2000,
          position: 'bottom'
        }).then(t => {
          t.present();
          if (this.redirect) {
            this.router.navigate(this.redirect).then(() => this.redirect = undefined);
          }
        });
      }
    });

    this.store.initialize([platform]);
  }

  async getIapProducts(showPasses = true) {
    // console.log('products', this.store.products);
    if (!showPasses) {
      const passIds = this.iapProducts.filter(x => x.type == CdvPurchase.ProductType.CONSUMABLE).map(x => x.id);
      return this.store.products.filter(x => !passIds.includes(x.id));
    } else
      return this.store.products;
  }

  async buyProduct(product: CdvPurchase.Product, rodeoId: number = 0) {
    //create the loader but don't show until verifying
    this.loading.create({
      message: 'Please wait while we validate your purchase.',
    }).then(loader => this.loader = loader);

    if (rodeoId && rodeoId > 0 && product.id.includes('passes')) {
      this.rodeoId = rodeoId;
      this.store.validator = `${this.api.endpoints.memberships}/VerifyPurchaseReceipt?rodeoId=${this.rodeoId}&accountId=${this.accountId}&productId=${product.id}`;
    } else {
      this.rodeoId = 0;
      this.store.validator = `${this.api.endpoints.memberships}/VerifyPurchaseReceipt?accountId=${this.accountId}&productId=${product.id}`;
    }

    await this.store.order(product.getOffer());
  }

  async restorePurchases() {
    const { store } = CdvPurchase;
    store.restorePurchases();
  }

  private buildProducts(platform: CdvPurchase.Platform) {
    this.iapProducts = [
      {
        id: 'com.nextgenrodeo.passes.event',
        type: CdvPurchase.ProductType.CONSUMABLE,
        platform
      },
      // {
      //   id: platform == CdvPurchase.Platform.APPLE_APPSTORE ? 'com.nextgenrodeo.passes.eventExtended' : 'com.nextgenrodeo.passes.eventextended',
      //   type: CdvPurchase.ProductType.CONSUMABLE,
      //   platform
      // },
      {
        id: 'com.nextgenrodeo.memberships.monthly',
        type: CdvPurchase.ProductType.PAID_SUBSCRIPTION,
        platform
      },
      {
        id: 'com.nextgenrodeo.memberships.yearly',
        type: CdvPurchase.ProductType.PAID_SUBSCRIPTION,
        platform
      }
    ];
  }
  //#endregion

  async getOrganizations() {
    return await this.api.getWithAuth<Organization[]>(`${this.api.endpoints.memberships}/GetAllOrganizationMemberships`);
  }

  async getOrganizationMemberships(orgId: number) {
    return await this.api.getWithAuth<Membership[]>(`${this.api.endpoints.memberships}/GetOrganizationMemberships?orgId=${orgId}`);
  }

  async getUserMemberships(riderIds: number[] = [this.accountId], orgId = 0, rodeoId = 0) {
    if (!this.accountId && riderIds.length == 0) return [];
    return await this.api.postWithAuth<AccountMembership[]>(`${this.api.endpoints.memberships}/GetAllAccountMemberships?orgId=${orgId}&rodeoId=${rodeoId}`, riderIds);
  }

  async updateAutoRenewDetails(autoRenewRequest: any) {
    return this.api.postWithAuth(`${this.api.endpoints.memberships}/UpdateAutoRenewDetails`, autoRenewRequest);
  }

  hasNgSubscription(memberships: AccountMembership[], rodeoId: number = 0) {
    // todo: adapt to read the product ID to evaluate entitlement
    const activeMemberships = memberships.filter(m => m.membership.inAppPurchaseId);
    
    if (activeMemberships.length == 0) {
      return false;
    } else {
      const hasNonPermitMembership = activeMemberships.find(x => !x.permit);
      if (hasNonPermitMembership) {
        return true;
      } else {
        if (rodeoId) {
          const hasRodeoPermit = activeMemberships.find(x => x.permit && x.rodeoId == Number(rodeoId))
          if (hasRodeoPermit) {
            return true;
          }
        }
      }
      return false;
    }
  }

  private fixForMissingAndroidPurchaseTokens(transaction: CdvPurchase.Transaction) {
    this.getUserMemberships()
    .then(memberships => {
      const accountMembership = memberships.find(x => x.membership != null && x.membership.inAppPurchaseId == transaction.products[0].id);
      if (!accountMembership) {
        //this shouldn't happen...
        this.insights.logEvent('IAP - MISSING ACCOUNTMEMBERSHIP', {
          accountId: this.accountId,
          platform: transaction.platform,
          validatorEndpoint: this.store.validator?.toString(),
          transaction
        });
      } else if (!accountMembership.playStorePurchaseToken) {
        this.insights.logEvent('IAP - MISSING PURCHASE TOKEN', {
          accountMembership,
          transaction,
          platform: transaction.platform
        });
        
        //send purchasetoken to api for saving
        this.updatePurchaseToken(accountMembership.accountMembershipId, transaction.purchaseId)
        .then(() => {
          this.insights.logEvent('IAP - UPDATED PURCHASE TOKEN', {
            accountMembership,
            transaction,
            platform: transaction.platform
          });
        })
        .catch(err => {
          this.insights.logEvent('IAP - PURCHASE TOKEN ERROR', {
            accountMembership,
            transaction,
            platform: transaction.platform, 
            err
          });
        });
      } else if (accountMembership.playStorePurchaseToken != transaction.purchaseId) {
        //conflicting purchase tokens? not sure this would really happen...
        this.insights.logEvent('IAP - CONFLICTING PURCHASE TOKEN', {
          accountMembership,
          transaction,
          platform: transaction.platform
        });
      }
    });
  }

  private async updatePurchaseToken(accountMembershipId: number, playStorePurchaseToken: string) {
    await this.api.postWithAuth(`${this.api.endpoints.memberships}/UpdateAndroidPurchaseToken`, {accountMembershipId, playStorePurchaseToken});
  }
  
}